import { useState, ChangeEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { showSuccess, showError } from "@/utils/toast";
import { Upload, Trash2, Plus, Calendar } from "lucide-react";

interface Country {
  id: number;
  code: string;
  translations: { [key: string]: string[] };
}

interface Shop {
  id: number;
  name: string;
  country_code: string;
  image_url: string | null;
}

interface DateRange {
  id: number;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

interface PdfUploaderProps {
  onUploadSuccess?: () => void;
}

const PdfUploader = ({ onUploadSuccess }: PdfUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | "">();
  const [selectedShop, setSelectedShop] = useState<number | "">();
  const [dateRanges, setDateRanges] = useState<DateRange[]>([]);
  const [loading, setLoading] = useState(false);

  // Országok betöltése
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch("/api/app/countries");
        if (response.ok) {
          const data = await response.json();
          setCountries(data.countries || []);
        } else {
          showError("Nem sikerült az országok betöltése.");
        }
      } catch (error) {
        console.error("Hiba az országok betöltésekor:", error);
        showError("Hiba az országok betöltésekor.");
      }
    };

    fetchCountries();
  }, []);

  // Boltok betöltése ország kiválasztása után
  useEffect(() => {
    if (!selectedCountry) {
      setShops([]);
      setSelectedShop("");
      return;
    }

    const fetchShops = async () => {
      try {
        const response = await fetch(`/api/app/shops?country_code=${selectedCountry}`);
        if (response.ok) {
          const data = await response.json();
          setShops(data.shops || []);
          setSelectedShop(""); // Alaphelyzetbe állítja a bolt kiválasztást
        } else {
          showError("Nem sikerült a boltok betöltése.");
          setShops([]);
        }
      } catch (error) {
        console.error("Hiba a boltok betöltésekor:", error);
        showError("Hiba a boltok betöltésekor.");
        setShops([]);
      }
    };

    fetchShops();
  }, [selectedCountry]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
      } else {
        showError("Kérlek, csak PDF fájlt válassz ki.");
        setSelectedFile(null);
        event.target.value = "";
      }
    }
  };

  const addDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    const newDateRange: DateRange = {
      id: Date.now(),
      startDate: today,
      endDate: today,
    };
    setDateRanges([...dateRanges, newDateRange]);
  };

  const removeDateRange = (id: number) => {
    setDateRanges(dateRanges.filter((range) => range.id !== id));
  };

  const updateDateRange = (
    id: number,
    field: 'startDate' | 'endDate',
    value: string
  ) => {
    setDateRanges(
      dateRanges.map((range) => {
        if (range.id === id) {
          const updated = { ...range, [field]: value };
          // Ellenőrzés: ha a kezdés után van a vége
          if (field === 'startDate' && updated.endDate < value) {
            updated.endDate = value;
          }
          if (field === 'endDate' && updated.startDate > value) {
            updated.startDate = value;
          }
          return updated;
        }
        return range;
      })
    );
  };

  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split('-');
    return `${year}. ${month}. ${day}.`;
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showError("Nincs fájl kiválasztva a feltöltéshez.");
      return;
    }

    if (!selectedCountry) {
      showError("Kérlek, válassz ki egy országot.");
      return;
    }

    if (!selectedShop) {
      showError("Kérlek, válassz ki egy boltot.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("pdf", selectedFile);
      formData.append("country_id", selectedCountry.toString());
      formData.append("shop_id", selectedShop.toString());

      // Dátum tartományok hozzáadása (JSON-ként)
      if (dateRanges.length > 0) {
        formData.append("date_ranges", JSON.stringify(dateRanges));
      }

      const response = await fetch("/api/admin/upload-pdf", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        showSuccess(`"${selectedFile.name}" sikeresen feltöltve!`);
        setSelectedFile(null);
        setSelectedCountry("");
        setSelectedShop("");
        setDateRanges([]);

        // A file input mező ürítése
        const fileInput = document.getElementById("pdf-file") as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }

        // Callback meghívása a lista frissítéséhez
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        const error = await response.json();
        showError(error.message || "Hiba a feltöltés során.");
      }
    } catch (error) {
      console.error("Hiba a feltöltés során:", error);
      showError("Hiba a feltöltés során.");
    } finally {
      setLoading(false);
    }
  };

  const getCountryName = (country: Country) => {
    return country.translations.HU?.[0] || country.code;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>PDF Feltöltése</CardTitle>
        <CardDescription>
          Válassz ki egy PDF fájlt, egy országot és egy boltot, majd töltsd fel.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* PDF Fájl kiválasztás */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="pdf-file">PDF Fájl</Label>
          <Input
            id="pdf-file"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
          />
          {selectedFile && (
            <p className="text-sm text-green-600">
              Kiválasztott: {selectedFile.name}
            </p>
          )}
        </div>

        {/* Ország kiválasztás */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="country-select">Ország</Label>
          <select
            id="country-select"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value ? parseInt(e.target.value) : "")}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">-- Válassz egy országot --</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {getCountryName(country)} ({country.code})
              </option>
            ))}
          </select>
        </div>

        {/* Bolt kiválasztás (csak ha van kiválasztott ország) */}
        {selectedCountry && (
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="shop-select">Bolt</Label>
            <select
              id="shop-select"
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value === "" ? "" : Number(e.target.value))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">-- Válassz egy boltot --</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Dátum tartományok */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Akció időtartama (Opcionális)</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDateRange}
            >
              <Plus className="mr-2 h-4 w-4" />
              Időtartam hozzáadása
            </Button>
          </div>

          {dateRanges.map((range, index) => (
            <div
              key={range.id}
              className="border rounded-lg p-4 space-y-4 bg-slate-900 dark:bg-slate-800"
            >
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">
                  Időtartam #{index + 1}: {formatDateForDisplay(range.startDate)} - {formatDateForDisplay(range.endDate)}
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDateRange(range.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`start-date-${range.id}`} className="text-xs">
                    <Calendar className="inline h-3 w-3 mr-1 text-white" />
                    Kezdés dátuma
                  </Label>
                  <Input
                    id={`start-date-${range.id}`}
                    type="date"
                    value={range.startDate}
                    onChange={(e) =>
                      updateDateRange(range.id, "startDate", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`end-date-${range.id}`} className="text-xs">
                    <Calendar className="inline h-3 w-3 mr-1 text-white" />
                    Vége dátuma
                  </Label>
                  <Input
                    id={`end-date-${range.id}`}
                    type="date"
                    value={range.endDate}
                    min={range.startDate}
                    onChange={(e) =>
                      updateDateRange(range.id, "endDate", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feltöltés gomb */}
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || !selectedCountry || !selectedShop || loading}
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          {loading ? "Feltöltés folyamatban..." : "Feltöltés"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PdfUploader;